import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import type { SearchableInvoice, PaymentMethod, Transaction } from './collectionMockData';
import { fetchStudents, fetchStudent, fetchClasses, fetchSections, mapStudentToUi, studentTransportRouteId } from '../../services/studentApi';
import type { ClassOption, SectionOption } from '../../services/studentApi';
import { fetchFeeStructures } from '../../services/feeStructureApi';
import { getSchoolId, getAcademicSessionId } from '../../services/auth';
import { apiRequest } from '../../services/api';
import { useCurrentSchool } from '../../hooks/useCurrentSchoolName';
import RupeeIcon from '../icons/RupeeIcon';
import type { InvoiceApiRecord, InvoiceDetailItem } from '../../services/invoiceApi';
import { fetchFeeHeadsApi } from '../../services/feeHeadApi';
import type { FeeHeadRecord } from '../../services/feeHeadApi';
import { fetchTransportRoutes } from '../../services/transportRouteApi';
import type { TransportRouteRecord } from '../../services/transportRouteApi';
import { fetchAcademicSessions } from '../../services/examApi';
import type { AcademicSessionRecord } from '../../services/examApi';
import { fetchTransportAllocations } from '../../services/transportAllocationApi';
import type { TransportAllocationRecord } from '../../services/transportAllocationApi';
import { createFeeTransaction, createMonthlyFeeTransaction } from '../../services/transactionApi';
import type { FeeTransactionLineItem } from '../../services/transactionApi';
import { printReceipt, toReceiptStudent } from '../../utils/printReceipt';
import type { ReceiptStudent, ReceiptData } from '../../utils/printReceipt';
import { breakdownFromApiItems, buildFeeBreakdown, fallbackBreakdown } from '../../utils/feeBreakdown';
import type { FeeBreakdown, FeeCharge, FeeDiscount } from '../../utils/feeBreakdown';
import {
    sessionMonths, buildMonthCells, monthsTotal, estimateMonthlyFee,
    formatMonth, formatMonthRange, monthsReceiptParts,
} from './months';
import type { MonthCell } from './months';
import AdditionalFees from './AdditionalFees';
import {
    round2, formatCurrency, routeFare, makeLineItem,
    lineItemGross, lineItemDiscount, lineItemNet, lineItemsNetOverMonths,
} from './feeLines';
import type { DraftLineItem, LineItemType } from './feeLines';
import './CollectionDesk.css';

interface StudentInvoiceApi {
    id: string;
    invoice_number: string;
    title: string;
    type: string;
    status: string;
    total_amount: string;
    paid_amount: string;
    balance_amount: string;
    issue_date: string;
    due_date: string;
    student: { id: string; name: string | null; enrollment_number: string };
    academic_session: { id: string; name: string };
    items?: InvoiceDetailItem[];
}

/** Split invoice line items into gross charges + grouped discounts.
    Falls back to a single line (the invoice title/total) when items are absent. */
function invoiceBreakdown(
    items: InvoiceDetailItem[] | undefined,
    fallbackName: string,
    fallbackAmount: number,
): FeeBreakdown {
    return items && items.length > 0
        ? breakdownFromApiItems(items)
        : fallbackBreakdown(fallbackName, fallbackAmount);
}

function formatDate(iso: string): string {
    return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

const PAYMENT_METHODS: { id: PaymentMethod; label: string; icon: string }[] = [
    { id: 'cash', label: 'Cash', icon: '💵' },
    { id: 'cheque', label: 'Cheque', icon: '📄' },
    { id: 'online', label: 'Online', icon: '🌐' },
    { id: 'bank_transfer', label: 'Bank Transfer', icon: '🏦' },
    { id: 'pos', label: 'POS', icon: '💳' },
];


type StudentUi = ReturnType<typeof mapStudentToUi>;

/** The receipt fields the desk already holds for the selected student. The months
    response isn't documented to embed a student, so the receipt falls back to this. */
function studentUiToReceipt(s: StudentUi): ReceiptStudent {
    const cls = s.class_name ?? undefined;
    const section = s.section_name ?? undefined;
    return {
        studentName: s.student_name || '—',
        studentEnrollment: s.enrollment_number ?? undefined,
        studentClass: cls && section ? `${cls} · ${section}` : cls,
        fatherName: s.father_name ?? undefined,
        motherName: s.mother_name ?? undefined,
        contactNumber: s.father_contact ?? undefined,
        address: s.address ?? undefined,
        photoUrl: s.photo_url ?? undefined,
    };
}

/** What the success toast can reprint. The invoice pane rebuilds its receipt at print
    time from the invoice on screen; the month grid has already composed one. */
type LastReceipt =
    | { kind: 'invoice'; tx: Transaction; invoice: SearchableInvoice; student: ReceiptStudent }
    | { kind: 'receipt'; data: ReceiptData };

export default function CollectionDesk() {
    const location = useLocation();
    const navigate = useNavigate();
    const presetHandled = useRef(false);
    const school = useCurrentSchool();
    const [selectedInvoice, setSelectedInvoice] = useState<SearchableInvoice | null>(null);

    const [amountPaid, setAmountPaid] = useState<number | ''>('');
    const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('cash');
    const [referenceNumber, setReferenceNumber] = useState('');
    const [amountError, setAmountError] = useState('');
    const [collecting, setCollecting] = useState(false);
    const [successMsg, setSuccessMsg] = useState('');
    const [lastTx, setLastTx] = useState<LastReceipt | null>(null);

    /* ── Monthly collection ──────────────────────────────────────────────────
       Months, not invoices, are what a collector is asked for at the desk ("three
       months of fees"). The backend takes the months and settles their invoices
       oldest-first, raising one for a month that was never billed — so this grid can
       offer months ahead of billing, which the invoice cards below simply cannot. */
    const [session, setSession] = useState<AcademicSessionRecord | null>(null);
    const [selectedMonths, setSelectedMonths] = useState<string[]>([]);
    const [monthsAmount, setMonthsAmount] = useState<number | ''>('');
    /* What an unbilled month is expected to cost: class fee structure + transport fare.
       Only an estimate — the invoice doesn't exist until the payment raises it. */
    const [monthEstimate, setMonthEstimate] = useState(0);
    const [monthsCollecting, setMonthsCollecting] = useState(false);
    const [monthsError, setMonthsError] = useState('');

    /* ── Additional fees (extra charges the collector adds to the invoice at collection) ── */
    const [lineItems, setLineItems] = useState<DraftLineItem[]>([]);
    const [lineItemErrors, setLineItemErrors] = useState<Record<string, Record<string, string>>>({}); // keyed by item.key
    const [lineItemsError, setLineItemsError] = useState(''); // general (total mismatch / invoice)
    const [feeHeads, setFeeHeads] = useState<FeeHeadRecord[]>([]);
    const [feeHeadsLoading, setFeeHeadsLoading] = useState(false);
    /* Standard amount per fee head, from the fee structure for this student's class
       & session. Prefills a row's amount when the head is ticked; heads with no
       component configured are simply absent and left for the collector to type. */
    const [standardAmounts, setStandardAmounts] = useState<Record<string, number>>({});
    /* Configured transport routes — offered as radios on a transport line. */
    const [routes, setRoutes] = useState<TransportRouteRecord[]>([]);
    const [routesLoading, setRoutesLoading] = useState(false);

    /* ── The student's own transport route ───────────────────────────────────
       Transport is not part of a class fee structure, so an invoice — raised in advance, or
       raised by the payment itself — can carry no transport item at all, and the fare of a
       student who rides the bus every day would simply never be asked for. The route is only
       on the student's detail record, so the desk fetches it and puts the fare on the fee
       builder ready to collect. */
    const [studentRouteId, setStudentRouteId] = useState<string | null>(null);
    /* The fare the student's own transport allocation sets — a stop's fee, or one set for this
       student alone — which is the fare to charge when there is one, ahead of the route's
       default. `null` until it's known, so the prefill waits for it rather than fall back to
       the route default and charge the wrong figure. */
    const [allocationFare, setAllocationFare] = useState<number | null>(null);
    /* Which fee builder the transport row has been seeded into. A collector who deletes the
       row means it, so it is offered once per builder and never grows back. */
    const transportSeeded = useRef<string | null>(null);

    /* ── Filters ── */
    const [filterClass, setFilterClass] = useState('');
    const [filterSection, setFilterSection] = useState('');
    const [filterStudentName, setFilterStudentName] = useState('');

    const [students, setStudents] = useState<StudentUi[]>([]);
    const [loadingStudents, setLoadingStudents] = useState(false);
    const [studentsSearched, setStudentsSearched] = useState(false);
    const [searchError, setSearchError] = useState('');
    /* Transport allocations for the searched students — the list shows the same route/fee
       columns as the Passenger List, so it needs the same data. */
    const [studentAllocations, setStudentAllocations] = useState<TransportAllocationRecord[]>([]);

    /* ── Selected student invoices (inline) ── */
    const [selectedStudent, setSelectedStudent] = useState<StudentUi | null>(null);
    const [studentInvoices, setStudentInvoices] = useState<StudentInvoiceApi[]>([]);
    const [invoicesLoading, setInvoicesLoading] = useState(false);
    const [selectedApiInvoice, setSelectedApiInvoice] = useState<StudentInvoiceApi | null>(null);

    const [classes, setClasses] = useState<ClassOption[]>([]);
    const [availableSections, setAvailableSections] = useState<SectionOption[]>([]);

    /* ── Load classes on mount (same API as Students list) ── */
    useEffect(() => {
        fetchClasses()
            .then((list) => setClasses(list))
            .catch(() => { /* silently fall back to empty */ });
    }, []);

    /* ── The session the month grid spans. Prefer the one the title bar has switched to;
       fall back to whichever the backend marks current. ── */
    useEffect(() => {
        fetchAcademicSessions()
            .then((list) => {
                const active = getAcademicSessionId();
                setSession(
                    list.find((s) => s.id === active)
                    ?? list.find((s) => s.is_current)
                    ?? list[0]
                    ?? null
                );
            })
            .catch(() => setSession(null));
    }, []);

    /* ── Cascade: reload sections when class selection changes ── */
    useEffect(() => {
        if (filterClass) {
            fetchSections(filterClass)
                .then((sections) => setAvailableSections(sections))
                .catch(() => setAvailableSections([]));
        } else {
            setAvailableSections([]);
        }
    }, [filterClass]);

    const handleStudentSearch = useCallback(async () => {
        if (!filterStudentName.trim() && !filterClass && !filterSection) return;
        setLoadingStudents(true);
        setStudentsSearched(true);
        setSelectedStudent(null);
        setStudentInvoices([]);
        setSearchError('');
        try {
            const [res, allocations] = await Promise.all([
                fetchStudents({
                    name: filterStudentName.trim() || undefined,
                    classId: filterClass || undefined,
                    sectionId: filterSection || undefined,
                    perPage: 50,
                }),
                // Route/fee columns are best-effort — a student with no allocation just shows "—".
                fetchTransportAllocations({ perPage: 200 }).catch(() => [] as TransportAllocationRecord[]),
            ]);
            setStudents(res.data.map(mapStudentToUi));
            setStudentAllocations(allocations);
        } catch (err: unknown) {
            const msg = (err && typeof err === 'object' && 'message' in err) ? (err as { message: string }).message : 'Failed to search students.';
            setSearchError(msg);
            setStudents([]);
            setStudentAllocations([]);
        } finally {
            setLoadingStudents(false);
        }
    }, [filterStudentName, filterClass, filterSection]);

    const resetMonths = useCallback(() => {
        setSelectedMonths([]);
        setMonthsAmount('');
        setMonthsError('');
        // The fee builder is bound to the selected month — it can't outlive the selection.
        setLineItems([]);
        setLineItemErrors({});
        setLineItemsError('');
        transportSeeded.current = null;
    }, []);

    /* The route is on the detail record only, so selecting a student costs a second call. */
    const loadStudentRoute = useCallback((studentId: string) => {
        setStudentRouteId(null);
        return fetchStudent(studentId)
            .then((s) => {
                setStudentRouteId(studentTransportRouteId(s));
                return s;
            })
            .catch(() => {
                /* No route → no prefill; the collector adds transport by hand as before. */
                setStudentRouteId(null);
                return null;
            });
    }, []);

    /* Everything the student's collection panel is built from — the invoice list *and* the
       month grid, which reads these to know what each month owes. A student selected without
       them would show every month as unbilled, so no path may skip this. */
    const loadStudentInvoices = useCallback(async (studentId: string) => {
        setInvoicesLoading(true);
        setStudentInvoices([]);
        try {
            const res = await apiRequest<StudentInvoiceApi[]>(`/api/student-invoices?student_id=${studentId}`);
            setStudentInvoices(res.data);
        } catch {
            setStudentInvoices([]);
        } finally {
            setInvoicesLoading(false);
        }
    }, []);

    const handleStudentClick = useCallback(async (student: StudentUi) => {
        setSelectedStudent(student);
        resetMonths();
        loadStudentRoute(student.id);
        await loadStudentInvoices(student.id);
    }, [resetMonths, loadStudentInvoices, loadStudentRoute]);

    const clearStudentInvoices = useCallback(() => {
        setSelectedStudent(null);
        setStudentInvoices([]);
        resetMonths();
    }, [resetMonths]);

    const handleInvoiceSelect = useCallback((inv: StudentInvoiceApi) => {
        if (!selectedStudent) return;
        const breakdown = invoiceBreakdown(inv.items, inv.title, parseFloat(inv.total_amount));
        const mapped: SearchableInvoice = {
            id: inv.id,
            invoice_number: inv.invoice_number,
            student_name: selectedStudent.student_name,
            student_enrollment: inv.student.enrollment_number ?? selectedStudent.enrollment_number ?? '',
            class_name: selectedStudent.class_name ?? '',
            section_name: selectedStudent.section_name ?? '',
            title: inv.title,
            issue_date: inv.issue_date,
            total_amount: parseFloat(inv.total_amount),
            paid_amount: parseFloat(inv.paid_amount),
            balance_amount: parseFloat(inv.balance_amount),
            status: parseFloat(inv.balance_amount) <= 0 ? 'published' : parseFloat(inv.paid_amount) > 0 ? 'partial' : 'overdue',
            items: breakdown.charges,
            discounts: breakdown.discounts,
            subtotal: breakdown.subtotal,
        };
        setSelectedApiInvoice(inv);
        selectInvoice(mapped);
    }, [selectedStudent]);

    const amountRef = useRef<HTMLInputElement>(null);
    const searchRef = useRef<HTMLInputElement>(null);

    const lineItemsNetTotal = round2(lineItems.reduce((sum, it) => sum + lineItemNet(it), 0));
    const totalDue = selectedInvoice ? round2(selectedInvoice.balance_amount + lineItemsNetTotal) : 0;
    /* Fee heads already on the selected invoice — an added fee must not duplicate one */
    const invoiceFeeHeadIds = new Set((selectedApiInvoice?.items ?? []).map((it) => it.fee_head_id));

    /** Display label for an added line (used in the left preview & receipt fallback). */
    const lineItemName = (it: DraftLineItem): string => {
        if (it.type === 'transport') {
            const route = routes.find((r) => r.id === it.routeId);
            return route ? `Transport — ${route.title}` : 'Transport Fee';
        }
        return it.feeHeadMode === 'existing'
            ? (feeHeads.find((h) => h.id === it.feeHeadId)?.name ?? 'Fee')
            : (it.feeHeadName.trim() || 'New Fee');
    };

    /* One cell per *billed* month of the session — a month with a generated invoice, carrying
       what that month still owes. Months not yet invoiced are dropped: the desk collects only
       against invoices that exist, never raising one for an unbilled month on the fly. */
    const monthCells: MonthCell[] = useMemo(
        () => (session
            ? buildMonthCells(sessionMonths(session.start_date, session.end_date), studentInvoices, monthEstimate)
                .filter((c) => c.status !== 'unbilled')
            : []),
        [session, studentInvoices, monthEstimate],
    );

    /**
     * Fee heads already charged on a selected month's invoice.
     *
     * The API rejects the whole payment if an added fee duplicates a head any target month
     * already carries, so the builder locks those heads out rather than letting the
     * collector build a payment the server will refuse. It's a union across the selection:
     * a head on *any* selected month is off-limits for all of them.
     */
    const takenFeeHeadIds = useMemo(() => {
        const picked = new Set(selectedMonths);
        const invoiceIds = new Set(
            monthCells.filter((c) => picked.has(c.month)).flatMap((c) => c.invoiceIds),
        );
        const taken = new Set<string>();
        for (const inv of studentInvoices) {
            if (!invoiceIds.has(inv.id)) continue;
            for (const item of inv.items ?? []) taken.add(item.fee_head_id);
        }
        return taken;
    }, [selectedMonths, monthCells, studentInvoices]);

    /* The invoice's own charges plus the fees being added now, presented gross-first
       with every discount — the invoice's and the ad-hoc ones — pulled into one group. */
    const draftLines = lineItems.filter((it) => lineItemGross(it) > 0);
    const draftCharges: FeeCharge[] = draftLines.map((it) => ({ name: lineItemName(it), amount: lineItemGross(it) }));
    const draftDiscounts: FeeDiscount[] = draftLines
        .filter((it) => lineItemDiscount(it) > 0)
        .map((it) => ({
            name: `${it.discountName.trim() || 'Discount'} — ${lineItemName(it)}`,
            amount: lineItemDiscount(it),
        }));
    const overview: FeeBreakdown = buildFeeBreakdown(
        [...(selectedInvoice?.items ?? []), ...draftCharges],
        [...(selectedInvoice?.discounts ?? []), ...draftDiscounts],
    );

    const resetLineItems = () => {
        setLineItems([]);
        setLineItemErrors({});
        setLineItemsError('');
        transportSeeded.current = null;
    };

    /* Keep the collected amount pinned to the balance + net of the added fees. */
    const syncAmountToItems = (items: DraftLineItem[]) => {
        if (!selectedInvoice) { syncMonthsAmount(selectedMonths, items); return; }
        const net = round2(items.reduce((s, it) => s + lineItemNet(it), 0));
        setAmountPaid(round2(selectedInvoice.balance_amount + net));
        setAmountError('');
    };

    const commitLineItems = (next: DraftLineItem[]) => {
        setLineItems(next);
        syncAmountToItems(next);
        if (lineItemsError) setLineItemsError('');
    };

    /* The draft line behind a ticked catalog head — absent when the head isn't ticked. */
    const feeHeadLine = (headId: string): DraftLineItem | undefined =>
        lineItems.find((it) => it.type === 'fee_head' && it.feeHeadMode === 'existing' && it.feeHeadId === headId);

    /* Tick/untick a catalog head. Ticking seeds the amount from the class fee structure. */
    const toggleFeeHead = (head: FeeHeadRecord, checked: boolean) => {
        const line = feeHeadLine(head.id);
        if (checked) {
            if (line) return;
            const standard = standardAmounts[head.id];
            commitLineItems([...lineItems, {
                ...makeLineItem('fee_head'),
                feeHeadId: head.id,
                feeHeadType: head.type ?? 'other',
                isOptional: head.is_optional,
                amount: typeof standard === 'number' ? standard : '',
            }]);
        } else if (line) {
            commitLineItems(lineItems.filter((it) => it.key !== line.key));
            setLineItemErrors((prev) => { const n = { ...prev }; delete n[line.key]; return n; });
        }
    };

    /* The route catalog is only needed once a transport fare is being charged. */
    const loadRoutesIfNeeded = () => {
        if (routes.length > 0 || routesLoading) return;
        setRoutesLoading(true);
        fetchTransportRoutes()
            .then((list) => setRoutes(list.filter((r) => r.is_active)))
            .catch(() => setLineItemsError('Failed to load transport routes.'))
            .finally(() => setRoutesLoading(false));
    };

    /* Fees the checkbox list can't express: a head that isn't in the catalog, or a transport fare. */
    const addCustomLineItem = (type: LineItemType) => {
        const item = makeLineItem(type);
        if (type === 'fee_head') item.feeHeadMode = 'new';
        if (type === 'transport') loadRoutesIfNeeded();
        setLineItems((prev) => [...prev, item]);
        setLineItemsError('');
    };

    /* Picking a route fills in its configured fare — still editable, and the server
       honours the amount we send over the route's default. */
    const selectRoute = (key: string, route: TransportRouteRecord) => {
        const fare = routeFare(route);
        /* Once the collector picks the route themselves, the row is theirs — drop the note
           that says the desk put it there off the student's record. */
        updateLineItem(key, { routeId: route.id, prefilled: false, ...(fare === undefined ? {} : { amount: fare }) });
    };

    const updateLineItem = (key: string, patch: Partial<DraftLineItem>) => {
        commitLineItems(lineItems.map((it) => (it.key === key ? { ...it, ...patch } : it)));
        if (lineItemErrors[key]) {
            setLineItemErrors((prev) => { const n = { ...prev }; delete n[key]; return n; });
        }
    };

    /* A row's discount box is the only switch for its discount: empty/zero means none. */
    const updateLineDiscount = (key: string, raw: string) => {
        const amount = raw === '' ? '' : Number(raw);
        updateLineItem(key, {
            discountAmount: amount,
            discountEnabled: typeof amount === 'number' && amount > 0,
        });
    };

    const removeLineItem = (key: string) => {
        commitLineItems(lineItems.filter((it) => it.key !== key));
        setLineItemErrors((prev) => { const n = { ...prev }; delete n[key]; return n; });
    };

    /* The checkbox list is the primary way fees get added, so the catalog has to be on
       screen the moment a fee builder opens — it can't wait for a user action. Both the
       invoice pane and any month selection in the grid put one there. */
    const feeBuilderOpen = !!selectedInvoice || selectedMonths.length > 0;
    useEffect(() => {
        if (!feeBuilderOpen || feeHeads.length > 0) return;
        setFeeHeadsLoading(true);
        fetchFeeHeadsApi()
            .then((list) => setFeeHeads(list))
            .catch(() => setLineItemsError('Failed to load fee heads.'))
            .finally(() => setFeeHeadsLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [feeBuilderOpen]);

    /* Standard amounts come from the fee structure for this student's class. The open
       invoice names the session; in the month grid it's the session the grid spans. */
    const studentClassId = selectedStudent?.class_id ?? null;
    const invoiceSessionId = selectedApiInvoice?.academic_session.id ?? session?.id ?? null;
    useEffect(() => {
        if (!studentClassId || !invoiceSessionId) {
            setStandardAmounts({});
            return;
        }
        let cancelled = false;
        fetchFeeStructures({ school_id: getSchoolId() ?? undefined, academic_session_id: invoiceSessionId })
            .then((structures) => {
                if (cancelled) return;
                const forClass = structures.find((s) => s.class_id === studentClassId && s.is_active)
                    ?? structures.find((s) => s.class_id === studentClassId);
                const amounts: Record<string, number> = {};
                for (const c of forClass?.components ?? []) {
                    const amt = parseFloat(c.amount);
                    if (amt > 0) amounts[c.fee_head_id] = amt;
                }
                setStandardAmounts(amounts);
            })
            .catch(() => { if (!cancelled) setStandardAmounts({}); });
        return () => { cancelled = true; };
    }, [studentClassId, invoiceSessionId]);

    /* ── What an unbilled month is expected to cost ──
       The class's fee structure — the invoice the backend raises carries nothing else. The
       endpoint bills a month as a side effect of collecting it, so it can't be asked what a
       month would cost; this estimate is the only figure available before the money is taken.
       Transport is charged on top of it, as a line item (see the prefill below). */
    const gridSessionId = session?.id ?? null;
    const selectedStudentId = selectedStudent?.id ?? null;
    useEffect(() => {
        if (!studentClassId || !gridSessionId || !selectedStudentId) {
            setMonthEstimate(0);
            /* No student to look one up for — the route's own fare is all the prefill has. */
            setAllocationFare(0);
            return;
        }
        setAllocationFare(null);
        let cancelled = false;
        Promise.all([
            fetchFeeStructures({ school_id: getSchoolId() ?? undefined, academic_session_id: gridSessionId }),
            fetchTransportAllocations().catch(() => [] as TransportAllocationRecord[]),
        ])
            .then(([structures, allocations]) => {
                if (cancelled) return;
                const forClass = structures.find((s) => s.class_id === studentClassId && s.is_active)
                    ?? structures.find((s) => s.class_id === studentClassId);
                const allocation = allocations.find((a) =>
                    a.student_id === selectedStudentId
                    && a.academic_session_id === gridSessionId
                    && a.is_active);
                setMonthEstimate(estimateMonthlyFee(forClass));
                const fare = Number(allocation?.effective_monthly_fee ?? 0);
                setAllocationFare(Number.isFinite(fare) ? fare : 0);
            })
            .catch(() => { if (!cancelled) { setMonthEstimate(0); setAllocationFare(0); } });
        return () => { cancelled = true; };
    }, [studentClassId, gridSessionId, selectedStudentId]);

    /* ── Prefilling the transport fare ───────────────────────────────────────
       A student on a route is charged for it here, because nothing else does: transport is not
       in the class fee structure, so neither an invoice raised in advance nor one the payment
       itself raises carries a transport item, and the fare of a child who rides the bus every
       day would never be asked for.

       The one thing that silences it is an invoice that *does* carry a transport charge —
       charging it again would double the fare and the server would refuse the payment. */
    const studentRoute = useMemo(
        () => (studentRouteId ? routes.find((r) => r.id === studentRouteId) ?? null : null),
        [studentRouteId, routes],
    );

    /* What to charge: the fare set for this student, else the route's default. Mirrors the
       server's own precedence (allocation → stop → route default) so the figure on screen is
       the one it would have resolved. */
    const prefillFare = (route: TransportRouteRecord): number | undefined =>
        ((allocationFare ?? 0) > 0 ? (allocationFare as number) : routeFare(route));

    /* Fares are named by the route catalog, so it has to be there before one can be charged. */
    useEffect(() => {
        if (studentRouteId) loadRoutesIfNeeded();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [studentRouteId]);

    /** Does this invoice already bill transport? */
    const billsTransport = (inv: StudentInvoiceApi | null | undefined): boolean =>
        (inv?.items ?? []).some((it) => it.fee_head?.type === 'transport' && parseFloat(it.net_amount) > 0);

    /* Transport already billed on the invoices behind the selected months. */
    const monthsBillTransport = useMemo(() => {
        const picked = new Set(selectedMonths);
        const invoiceIds = new Set(
            monthCells.filter((c) => picked.has(c.month)).flatMap((c) => c.invoiceIds),
        );
        return studentInvoices.some((inv) => invoiceIds.has(inv.id) && billsTransport(inv));
    }, [selectedMonths, monthCells, studentInvoices]);

    useEffect(() => {
        const builder = selectedInvoice ? `inv:${selectedInvoice.id}`
            : selectedMonths.length > 0 && selectedStudent ? `months:${selectedStudent.id}`
                : null;
        if (!builder || transportSeeded.current === builder) return;

        /* The student's own fare outranks the route's, so wait for it rather than prefill the
           route default and charge the wrong figure. */
        if (allocationFare === null) return;

        const fare = studentRoute ? prefillFare(studentRoute) : undefined;
        /* No route, or no fare configured on either the student or the route — there is no
           figure to prefill, and a row with an empty amount would only block the payment. */
        if (!studentRoute || fare === undefined) return;

        /* Transport already on the invoice being collected, or on one behind a selected month. */
        if (selectedInvoice ? billsTransport(selectedApiInvoice) : monthsBillTransport) return;

        transportSeeded.current = builder;
        commitLineItems([...lineItems, {
            ...makeLineItem('transport'),
            routeId: studentRoute.id,
            amount: fare,
            prefilled: true,
        }]);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [studentRoute, selectedInvoice, selectedApiInvoice, selectedStudent, selectedMonths, allocationFare, monthsBillTransport]);


    const monthsDue = monthsTotal(monthCells, selectedMonths);
    /* A `per_month` fee is charged once per month, so what the fees add depends on how many
       months are selected — the same ₹300 fare is ₹300 over one month and ₹900 over three. */
    const monthsAdded = lineItemsNetOverMonths(lineItems, selectedMonths.length);
    const monthsTotalDue = round2(monthsDue + monthsAdded);
    const monthsCollectingNow = typeof monthsAmount === 'number' ? monthsAmount : 0;

    /* Pin the amount to what the selection owes, plus any fees being added to it. */
    const syncMonthsAmount = (months: string[], items: DraftLineItem[]) => {
        const total = round2(monthsTotal(monthCells, months) + lineItemsNetOverMonths(items, months.length));
        setMonthsAmount(total > 0 ? total : '');
        setMonthsError('');
    };

    /* Selecting a month re-pins the amount to what the selection owes — including the fees,
       whose total can move with the month count. The collector can still overtype it: less
       takes a part payment, more is held as an advance. */
    const toggleMonth = (cell: MonthCell) => {
        if (cell.status === 'paid') return;
        const next = selectedMonths.includes(cell.month)
            ? selectedMonths.filter((m) => m !== cell.month)
            : [...selectedMonths, cell.month].sort();
        setSelectedMonths(next);
        syncMonthsAmount(next, lineItems);
    };

    /* Client-side line-item validation; mirrors the server's field rules. */
    const validateLineItems = (): boolean => {
        const errs: Record<string, Record<string, string>> = {};
        lineItems.forEach((it) => {
            const e: Record<string, string> = {};
            const amt = typeof it.amount === 'number' ? it.amount : NaN;
            if (it.type === 'fee_head') {
                /* Ticked catalog rows carry their head id by construction; only a
                   custom line can be missing or mis-naming its head. */
                if (it.feeHeadMode === 'new') {
                    const nm = it.feeHeadName.trim();
                    if (!nm) e.feeHead = 'Enter a name for the new fee head.';
                    else if (nm.length > 100) e.feeHead = 'Keep the name under 100 characters.';
                }
                if (!(amt >= 0.01)) e.amount = 'Enter an amount (min ₹0.01).';
            } else if (!(amt >= 0.01)) {
                e.amount = 'Enter a transport fare (min ₹0.01).';
            }
            if (it.discountEnabled) {
                const d = typeof it.discountAmount === 'number' ? it.discountAmount : NaN;
                if (!(d >= 0.01)) e.discount = 'Enter a discount amount.';
                else if (amt >= 0.01 && d > amt) {
                    e.discount = it.type === 'transport'
                        ? 'Discount cannot exceed the fare.'
                        : 'Discount cannot exceed the fee amount.';
                }
            }
            /* A split has to give every month at least a paise, or the server would be asked
               to write zero-amount items its own validator rejects. Catch it here, where the
               collector can still fix it, rather than as a 422 after they've taken the cash. */
            const n = selectedMonths.length;
            if (it.distribution === 'split' && n > 1 && amt >= 0.01 && Math.round(amt * 100) < n) {
                e.amount = `${formatCurrency(amt)} cannot be split across ${n} months — each month must get at least ₹0.01.`;
            }
            if (Object.keys(e).length) errs[it.key] = e;
        });
        setLineItemErrors(errs);
        return Object.keys(errs).length === 0;
    };

    /* Map the server's field-keyed 422 errors (line_items.<i>.<field>) back onto rows.
       Returns any general (non-row) message. */
    const applyServerLineErrors = (errors?: Record<string, string[]>): string => {
        if (!errors) return '';
        const byKey: Record<string, Record<string, string>> = {};
        let general = '';
        Object.entries(errors).forEach(([field, msgs]) => {
            const msg = Array.isArray(msgs) ? msgs[0] : String(msgs);
            const m = field.match(/^line_items\.(\d+)\.(.+)$/);
            if (m) {
                const item = lineItems[Number(m[1])];
                if (!item) { general = general || msg; return; }
                const sub = m[2];
                const bucket = sub.startsWith('fee_head') ? 'feeHead'
                    : sub.startsWith('discount') ? 'discount'
                        : sub.startsWith('distribution') ? 'distribution'
                            : 'amount'; // amount / type both surface on the amount field
                byKey[item.key] = { ...(byKey[item.key] || {}), [bucket]: msg };
            } else {
                general = general || msg;
            }
        });
        setLineItemErrors(byKey);
        return general;
    };

    /**
     * The draft fee rows as the API's `line_items`.
     *
     * `distribution` is sent only when collecting for months — the API rejects it on a
     * single-invoice payment, which has nothing to distribute across.
     */
    const buildPayloadLineItems = (forMonths: boolean): FeeTransactionLineItem[] => lineItems.map((it) => {
        const discount = lineItemDiscount(it);
        const discountPatch = discount > 0
            ? { discount: { amount: discount, ...(it.discountName.trim() ? { name: it.discountName.trim() } : {}) } }
            : {};
        const distributionPatch = forMonths ? { distribution: it.distribution } : {};
        if (it.type === 'transport') {
            return {
                type: 'transport',
                amount: it.amount as number,
                ...(it.routeId ? { transport_route_id: it.routeId } : {}),
                ...discountPatch,
                ...distributionPatch,
            };
        }
        return {
            type: 'fee_head',
            amount: it.amount as number,
            is_optional: it.isOptional,
            ...(it.feeHeadMode === 'existing'
                ? { fee_head_id: it.feeHeadId }
                : { fee_head_name: it.feeHeadName.trim(), fee_head_type: it.feeHeadType }),
            ...discountPatch,
            ...distributionPatch,
        };
    });

    const selectInvoice = (inv: SearchableInvoice) => {
        setSelectedInvoice(inv);
        setAmountPaid(inv.balance_amount);
        setPaymentMethod('cash');
        setReferenceNumber('');
        setAmountError('');
        resetLineItems();
        setTimeout(() => amountRef.current?.focus(), 100);
    };

    const clearSelection = () => {
        setSelectedInvoice(null);
        setSelectedApiInvoice(null);
        setAmountPaid('');
        setAmountError('');
        setReferenceNumber('');
        resetLineItems();
        setTimeout(() => searchRef.current?.focus(), 100);
    };

    /* ── Opened from elsewhere: the Students list, or the Generated Invoices modal.
       With a student, land on their collection panel — months and invoices both. With an
       invoice, go straight to collecting it, but load the student behind it just the same,
       so closing the invoice (✕ Change invoice) drops back onto a real month grid rather
       than one where every month looks unbilled. ── */
    useEffect(() => {
        if (presetHandled.current) return;
        const nav = location.state as
            | { invoice?: InvoiceApiRecord; student?: { id: string; name?: string; enrollment_number?: string | null } }
            | null;
        const preset = nav?.invoice;
        const studentId = preset?.student_id ?? nav?.student?.id;
        if (!studentId) return;
        presetHandled.current = true;

        /* Named by whoever navigated here, then filled in from the API — the caller carries
           a name, not a class, and the class is what seeds a month's estimated fee. */
        const stub = (name: string, enrollment: string | null): StudentUi => ({
            id: studentId,
            student_name: name,
            email: null,
            enrollment_number: enrollment,
            roll_number: null,
            class_id: null,
            class_name: null,
            section_id: null,
            section_name: null,
            status: 'active',
            parent_name: null,
            parent_phone: null,
            parent_email: null,
            father_name: null,
            mother_name: null,
            father_contact: null,
            aadhaar_number: null,
            address: null,
            date_of_birth: null,
            admission_date: null,
            photo_url: null,
            documents: [],
            metadata: {},
        } as StudentUi);

        const studentUi = stub(
            preset?.student?.name ?? nav?.student?.name ?? '',
            preset?.student?.enrollment_number ?? nav?.student?.enrollment_number ?? null,
        );

        setSelectedStudent(studentUi);
        /* The month grid is built from these; without them there are no months to pick. */
        loadStudentInvoices(studentId);
        /* Fills in the class (which prices a month) and the route (which prices the fare) —
           the caller carries neither. */
        loadStudentRoute(studentId)
            .then((s) => {
                if (!s) return; // no class → no prefill; the collector types the amounts
                setSelectedStudent((prev) => (prev && prev.id === s.id ? { ...prev, ...mapStudentToUi(s) } : prev));
            });
        navigate('.', { replace: true, state: null });

        if (!preset) {
            /* Sent here for the student, not an invoice — leave the months panel open. */
            setPaymentMethod('cash');
            return;
        }

        const balance = parseFloat(preset.balance_amount);
        const paid = parseFloat(preset.paid_amount);

        const apiInvoice: StudentInvoiceApi = {
            id: preset.id,
            invoice_number: preset.invoice_number,
            title: preset.title,
            type: preset.type,
            status: preset.status,
            total_amount: preset.total_amount,
            paid_amount: preset.paid_amount,
            balance_amount: preset.balance_amount,
            issue_date: preset.issue_date,
            due_date: preset.due_date ?? '',
            student: {
                id: preset.student?.id ?? preset.student_id,
                name: preset.student?.name ?? null,
                enrollment_number: preset.student?.enrollment_number ?? '',
            },
            academic_session: { id: preset.academic_session.id, name: preset.academic_session.name },
            items: preset.items,
        };

        const presetBreakdown = invoiceBreakdown(preset.items, preset.title, parseFloat(preset.total_amount));
        const mapped: SearchableInvoice = {
            id: preset.id,
            invoice_number: preset.invoice_number,
            student_name: studentUi.student_name,
            student_enrollment: preset.student?.enrollment_number ?? '',
            class_name: '',
            section_name: '',
            title: preset.title,
            issue_date: preset.issue_date,
            total_amount: parseFloat(preset.total_amount),
            paid_amount: paid,
            balance_amount: balance,
            status: balance <= 0 ? 'published' : paid > 0 ? 'partial' : 'overdue',
            items: presetBreakdown.charges,
            discounts: presetBreakdown.discounts,
            subtotal: presetBreakdown.subtotal,
        };

        setSelectedApiInvoice(apiInvoice);
        setSelectedInvoice(mapped);
        setAmountPaid(balance);
        setPaymentMethod('cash');
        setReferenceNumber('');
        setAmountError('');
        setTimeout(() => amountRef.current?.focus(), 120);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /* ── Validation ── */
    const validateAmount = (val: number | '') => {
        if (val === '' || val <= 0) { setAmountError('Enter a valid amount.'); return false; }
        if (selectedInvoice && val > totalDue) {
            setAmountError(lineItemsNetTotal > 0
                ? `Amount cannot exceed total due (${formatCurrency(totalDue)}).`
                : `Amount cannot exceed balance (${formatCurrency(selectedInvoice.balance_amount)}).`);
            return false;
        }
        setAmountError('');
        return true;
    };

    /* ── Print receipt for a transaction ── */
    const handlePrintReceipt = useCallback((tx: Transaction, invoice: SearchableInvoice | null | undefined, student: ReceiptStudent) => {
        // Recomputed from the charges & discounts actually printed, so Subtotal − Discounts = Total on paper.
        const b = buildFeeBreakdown(invoice?.items ?? [], invoice?.discounts ?? []);
        printReceipt({
            ...student,
            schoolName: school.name ?? undefined,
            schoolAddress: school.address ?? undefined,
            invoiceNumber: tx.invoice_number,
            billingDate: invoice?.issue_date,
            items: b.charges.length > 0 ? b.charges : undefined,
            discounts: b.discounts,
            subtotal: b.subtotal,
            totalAmount: b.total,
            amountPaid: tx.amount_paid,
            // The server's balance for this invoice after the payment — not total − paid,
            // which would ignore what the student had already paid on it.
            balanceDue: invoice?.balance_amount,
            paymentMethod: tx.payment_method,
            referenceNumber: tx.reference_number,
            collectedAt: tx.collected_at,
        });
    }, [school]);

    /* ── Collect ── */
    const handleCollect = async () => {
        if (!selectedInvoice || !selectedStudent || !selectedApiInvoice) return;
        if (!validateAmount(amountPaid)) return;
        if ((paymentMethod === 'cheque' || paymentMethod === 'bank_transfer') && !referenceNumber.trim()) {
            setAmountError('Reference number is required for this payment method.');
            return;
        }
        if (lineItems.length > 0 && !validateLineItems()) {
            setLineItemsError('Fix the highlighted fees before collecting.');
            return;
        }
        setLineItemsError('');

        setCollecting(true);
        setAmountError('');

        const payloadLineItems = buildPayloadLineItems(false);

        const payload = {
            student_id: selectedStudent.id,
            academic_session_id: selectedApiInvoice.academic_session.id,
            invoice_id: selectedInvoice.id,
            transaction_date: new Date().toISOString().split('T')[0],
            amount_paid: amountPaid as number,
            payment_method: paymentMethod,
            reference_number: referenceNumber.trim() || null,
            status: 'success' as const,
            // Only sent together, per the API contract; the server re-validates the total.
            ...(payloadLineItems.length > 0
                ? { line_items: payloadLineItems, expected_line_items_total: lineItemsNetTotal }
                : {}),
        };

        try {
            const result = await createFeeTransaction(payload);

            const newTx: Transaction = {
                id: result.id,
                invoice_number: result.invoice.invoice_number,
                student_name: result.student.name ?? selectedInvoice.student_name,
                amount_paid: parseFloat(result.amount_paid),
                payment_method: result.payment_method,
                reference_number: result.reference_number ?? '',
                collected_at: result.created_at,
            };
            // Build the receipt from the server's refreshed invoice (existing + newly added
            // lines). When it omits them, fall back to what the overview just showed.
            const resultItems = result.invoice.items;
            const receiptBreakdown = resultItems && resultItems.length > 0
                ? breakdownFromApiItems(resultItems)
                : overview;
            const receiptInvoice: SearchableInvoice = {
                ...selectedInvoice,
                total_amount: parseFloat(result.invoice.total_amount),
                balance_amount: parseFloat(result.invoice.balance_amount),
                items: receiptBreakdown.charges,
                discounts: receiptBreakdown.discounts,
                subtotal: receiptBreakdown.subtotal,
            };
            const receiptStudent = toReceiptStudent(result.student, selectedInvoice.student_name);
            setSuccessMsg(`Payment of ${formatCurrency(parseFloat(result.amount_paid))} collected successfully!`);
            setLastTx({ kind: 'invoice', tx: newTx, invoice: receiptInvoice, student: receiptStudent });

            // Re-fetch invoices to reflect updated balances/status
            const studentToRefresh = selectedStudent;
            clearSelection();
            // Refresh the catalog so any newly created fee head is selectable next time.
            fetchFeeHeadsApi().then(setFeeHeads).catch(() => {});
            if (studentToRefresh) {
                setInvoicesLoading(true);
                apiRequest<StudentInvoiceApi[]>(`/api/student-invoices?student_id=${studentToRefresh.id}`)
                    .then((res) => setStudentInvoices(res.data))
                    .catch(() => {})
                    .finally(() => setInvoicesLoading(false));
            }

            setTimeout(() => { setSuccessMsg(''); setLastTx(null); }, 6000);
        } catch (err: unknown) {
            const apiErr = (err && typeof err === 'object') ? err as { message?: string; errors?: Record<string, string[]> } : {};
            const general = applyServerLineErrors(apiErr.errors);
            if (general) setLineItemsError(general);
            setAmountError(apiErr.message || 'Payment failed. Please try again.');
        } finally {
            setCollecting(false);
        }
    };

    /* ── Collect across months ──
       One payment, one total. The months are sent unsplit and the backend allocates them
       oldest-first; what comes back in `allocations` — not what we asked for — is what
       the receipt prints. */
    const handleCollectMonths = async () => {
        if (!selectedStudent || !session || selectedMonths.length === 0) return;

        const amount = typeof monthsAmount === 'number' ? monthsAmount : NaN;
        if (!(amount >= 0.01)) {
            setMonthsError('Enter an amount (min ₹0.01).');
            return;
        }
        if ((paymentMethod === 'cheque' || paymentMethod === 'bank_transfer') && !referenceNumber.trim()) {
            setMonthsError('Reference number is required for this payment method.');
            return;
        }
        if (lineItems.length > 0 && !validateLineItems()) {
            setLineItemsError('Fix the highlighted fees before collecting.');
            return;
        }

        try {
            const payloadLineItems = buildPayloadLineItems(true);
            const result = await createMonthlyFeeTransaction({
                student_id: selectedStudent.id,
                academic_session_id: session.id,
                months: [...selectedMonths].sort(),
                transaction_date: new Date().toISOString().split('T')[0],
                amount_paid: amount,
                payment_method: paymentMethod,
                reference_number: referenceNumber.trim() || null,
                status: 'success',
                /* Sent unsplit: the server divides a `split` row across the months in integer
                   paise. `expected_line_items_total` is the pre-split net it must add up to. */
                ...(payloadLineItems.length > 0
                    ? { line_items: payloadLineItems, expected_line_items_total: monthsAdded }
                    : {}),
            });

            const allocations = result.allocations ?? [];
            const paid = parseFloat(result.amount_paid) || 0;
            const advance = parseFloat(result.unallocated_amount) || 0;

            const receiptStudent = result.student
                ? toReceiptStudent(result.student, selectedStudent.student_name)
                : studentUiToReceipt(selectedStudent);

            const receipt: ReceiptData = {
                ...receiptStudent,
                schoolName: school.name ?? undefined,
                schoolAddress: school.address ?? undefined,
                /* The same builder the ledger reprints from, so the copy handed over now and
                   the copy printed from the record months later are the same paper. */
                ...monthsReceiptParts(allocations, advance),
                amountPaid: paid,
                paymentMethod: result.payment_method,
                referenceNumber: result.reference_number ?? referenceNumber.trim(),
                collectedAt: result.created_at ?? new Date().toISOString(),
            };

            /* A month the money never reached is absent from `allocations`. Saying so
               beats letting the collector assume all three months were cleared. */
            const covered = new Set(allocations.map((a) => a.month));
            const missed = selectedMonths.filter((m) => !covered.has(m));
            setSuccessMsg(
                `Payment of ${formatCurrency(paid)} collected.`
                + (advance > 0 ? ` ${formatCurrency(advance)} held as advance.` : '')
                + (missed.length > 0 ? ` Not covered: ${missed.map(formatMonth).join(', ')}.` : '')
            );
            setLastTx({ kind: 'receipt', data: receipt });

            const studentToRefresh = selectedStudent;
            resetMonths();
            setReferenceNumber('');
            // Refresh the catalog so any newly created fee head is selectable next time.
            if (payloadLineItems.length > 0) fetchFeeHeadsApi().then(setFeeHeads).catch(() => {});
            setInvoicesLoading(true);
            apiRequest<StudentInvoiceApi[]>(`/api/student-invoices?student_id=${studentToRefresh.id}`)
                .then((res) => setStudentInvoices(res.data))
                .catch(() => {})
                .finally(() => setInvoicesLoading(false));

            setTimeout(() => { setSuccessMsg(''); setLastTx(null); }, 8000);
        } catch (err: unknown) {
            const apiErr = (err && typeof err === 'object') ? err as { message?: string; errors?: Record<string, string[]> } : {};
            /* The server rejects the whole payment and writes nothing, so keep the selection
               and the fees. Row errors are keyed by the *request* row (`line_items.0.…`) even
               though one row became several invoice items, so they land back on the row the
               collector edited; anything else (a month error) surfaces as the general message. */
            const general = applyServerLineErrors(apiErr.errors);
            if (general) setLineItemsError(general);
            setMonthsError(general || apiErr.message || 'Payment failed. Please try again.');
        } finally {
            setMonthsCollecting(false);
        }
    };

    const needsReference = paymentMethod === 'cheque' || paymentMethod === 'bank_transfer';

    /* Overview echoes what's about to be taken and what remains after. */
    const collectingNow = typeof amountPaid === 'number' ? amountPaid : 0;
    const balanceAfter = round2(Math.max(0, totalDue - collectingNow));

    /* The active allocation per student, keyed for the list's Route/Monthly Fee columns —
       mirrors the Passenger List, which reads the same fields off the same record shape. */
    const allocationByStudent = useMemo(() => {
        const map = new Map<string, TransportAllocationRecord>();
        for (const a of studentAllocations) {
            if (a.is_active) map.set(a.student_id, a);
        }
        return map;
    }, [studentAllocations]);

    const studentRouteName = (a: TransportAllocationRecord | undefined): string =>
        a ? (a.route_stop?.route?.title ?? a.transport_route?.title ?? '') : '';

    const studentMonthlyFee = (a: TransportAllocationRecord | undefined): number | null => {
        if (!a) return null;
        if (a.custom_monthly_fee != null) return Number(a.custom_monthly_fee);
        const fee = Number(a.effective_monthly_fee ?? a.route_stop?.monthly_fee ?? a.transport_route?.default_monthly_fee ?? 0);
        return Number.isFinite(fee) ? fee : null;
    };

    return (
        <div className="collection">
            {successMsg && (
                <div className="collection__toast">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></svg>
                    <span className="collection__toast-msg">{successMsg}</span>
                    {lastTx && (
                        <button
                            className="collection__toast-print"
                            onClick={() => (lastTx.kind === 'invoice'
                                ? handlePrintReceipt(lastTx.tx, lastTx.invoice, lastTx.student)
                                : printReceipt(lastTx.data))}
                        >
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="6 9 6 2 18 2 18 9" />
                                <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
                                <rect x="6" y="14" width="12" height="8" />
                            </svg>
                            Print Receipt
                        </button>
                    )}
                    <button className="collection__toast-close" onClick={() => { setSuccessMsg(''); setLastTx(null); }}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                    </button>
                </div>
            )}

            <div className="collection__header">
                <div className="collection__header-text">
                    <h1 className="collection__title">Collection Desk</h1>
                    <p className="collection__subtitle">Rapid payment collection — search a student or invoice to begin.</p>
                </div>
            </div>

            {/* ── Toolbar (matching StudentListing) ── */}
            <div className="collection__toolbar">
                <div className="collection__toolbar-left">
                    {/* Name search */}
                    <div className="collection__search-wrapper">
                        <svg className="collection__search-icon-sm" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
                        </svg>
                        <input
                            ref={searchRef}
                            className="collection__filter-search"
                            type="text"
                            placeholder="Search by name…"
                            value={filterStudentName}
                            onChange={(e) => setFilterStudentName(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') handleStudentSearch(); }}
                        />
                        {filterStudentName && (
                            <button className="collection__filter-search-clear" onClick={() => setFilterStudentName('')} aria-label="Clear search">×</button>
                        )}
                    </div>

                    {/* Class filter */}
                    <select
                        className="collection__filter"
                        value={filterClass}
                        onChange={(e) => { setFilterClass(e.target.value); setFilterSection(''); }}
                    >
                        <option value="">All Classes</option>
                        {classes.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>

                    {/* Section filter — enabled only when a class is selected */}
                    <select
                        className="collection__filter"
                        value={filterSection}
                        onChange={(e) => setFilterSection(e.target.value)}
                        disabled={!filterClass}
                    >
                        <option value="">All Sections</option>
                        {availableSections.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </select>

                    {/* Search button */}
                    <button className="collection__search-btn-sm" onClick={handleStudentSearch}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
                        </svg>
                        Search
                    </button>

                    {/* Clear button — only shown when filters are active */}
                    {(filterClass || filterSection || filterStudentName) && (
                        <button
                            className="collection__clear-btn"
                            onClick={() => { setFilterClass(''); setFilterSection(''); setFilterStudentName(''); setStudents([]); setStudentsSearched(false); setSearchError(''); clearStudentInvoices(); }}
                        >Clear</button>
                    )}
                </div>

                <div className="collection__toolbar-right">
                    {studentsSearched && (
                        <span className="collection__record-count">{`${students.length} student${students.length !== 1 ? 's' : ''}`}</span>
                    )}
                </div>
            </div>

            {/* ── Student List ── */}
            {studentsSearched && !selectedInvoice && (
                <div className="collection__student-list">
                    {loadingStudents ? (
                        <div className="collection__student-loading">
                            <svg className="collection__spinner" width="22" height="22" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" strokeWidth="3" /></svg>
                            <span>Searching students…</span>
                        </div>
                    ) : searchError ? (
                        <div className="collection__student-empty">{searchError}</div>
                    ) : students.length === 0 ? (
                        <div className="collection__student-empty">No students found matching the applied filters.</div>
                    ) : (
                        <div className="collection__student-table-wrap">
                            <table className="collection__student-table">
                                <thead>
                                    <tr>
                                        <th className="collection__student-th">Name</th>
                                        <th className="collection__student-th">Class</th>
                                        <th className="collection__student-th">Father</th>
                                        <th className="collection__student-th">Mother</th>
                                        <th className="collection__student-th">Route</th>
                                        <th className="collection__student-th collection__student-th--amount">Monthly Fee</th>
                                        <th className="collection__student-th">Address</th>
                                        <th className="collection__student-th">Contact</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {students.map((s) => {
                                        const allocation = allocationByStudent.get(s.id);
                                        const routeName = studentRouteName(allocation);
                                        const monthlyFee = studentMonthlyFee(allocation);
                                        return (
                                            <tr
                                                className={`collection__student-row ${selectedStudent?.id === s.id ? 'collection__student-row--active' : ''}`}
                                                key={s.id}
                                            >
                                                <td className="collection__student-td collection__student-td--name">
                                                    <button className="collection__student-name-btn" onClick={() => handleStudentClick(s)}>{s.student_name}</button>
                                                </td>
                                                <td className="collection__student-td">{s.class_name || '—'}</td>
                                                <td className="collection__student-td">{s.father_name || <span className="collection__student-muted">—</span>}</td>
                                                <td className="collection__student-td">{s.mother_name || <span className="collection__student-muted">—</span>}</td>
                                                <td className="collection__student-td">{routeName || <span className="collection__student-muted">—</span>}</td>
                                                <td className="collection__student-td collection__student-td--amount">
                                                    {monthlyFee != null
                                                        ? (
                                                            <>
                                                                {formatCurrency(monthlyFee)}
                                                                {allocation?.custom_monthly_fee != null && <span className="collection__student-fee-override" title="Custom override">✱</span>}
                                                            </>
                                                        )
                                                        : <span className="collection__student-muted">—</span>}
                                                </td>
                                                <td className="collection__student-td">{s.address || <span className="collection__student-muted">—</span>}</td>
                                                <td className="collection__student-td">
                                                    {s.father_contact
                                                        ? <a href={`tel:${s.father_contact}`} className="collection__student-contact-link">{s.father_contact}</a>
                                                        : <span className="collection__student-muted">—</span>}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}

            {/* ── Inline Invoices for selected student ── */}
            {selectedStudent && !selectedInvoice && (
                <div className="collection__invoices">
                    <div className="collection__invoices-header">
                        <div>
                            <h3 className="collection__invoices-title">{selectedStudent.student_name}</h3>
                            <div className="collection__invoices-meta">
                                {selectedStudent.enrollment_number && <span>{selectedStudent.enrollment_number}</span>}
                                {selectedStudent.class_name && <span>{selectedStudent.class_name}{selectedStudent.section_name ? ` — ${selectedStudent.section_name}` : ''}</span>}
                                {session && <span>{session.name}</span>}
                            </div>
                        </div>
                        <button className="collection__left-close" onClick={clearStudentInvoices} title="Close invoices">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                        </button>
                    </div>

                    {/* ── Monthly fees — pick the months, hand over one total ── */}
                    <div className="collection__months">
                        <div className="collection__months-head">
                            <h4 className="collection__months-title">Monthly Fees</h4>
                            <span className="collection__months-hint">
                                {!session
                                    ? 'No academic session found — months cannot be listed.'
                                    : 'Pick the invoiced months being paid for.'}
                            </span>
                        </div>

                        {invoicesLoading ? (
                            <div className="collection__invoices-loading">
                                <svg className="collection__spinner" width="22" height="22" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" strokeWidth="3" /></svg>
                                <span>Loading months…</span>
                            </div>
                        ) : monthCells.length > 0 ? (
                            <div className="collection__month-grid">
                                {monthCells.map((cell) => {
                                    const on = selectedMonths.includes(cell.month);
                                    const settled = cell.status === 'paid';
                                    return (
                                        <button
                                            key={cell.month}
                                            type="button"
                                            className={`collection__month collection__month--${cell.status} ${on ? 'collection__month--on' : ''}`}
                                            onClick={() => toggleMonth(cell)}
                                            disabled={settled}
                                            aria-pressed={on}
                                            title={settled ? `${cell.label} — fully paid` : cell.label}
                                        >
                                            <span className="collection__month-name">{cell.short}</span>
                                            <span className="collection__month-year">{cell.year}</span>
                                            <span className="collection__month-due">
                                                {settled ? 'Paid' : formatCurrency(cell.due)}
                                            </span>
                                            <span className="collection__month-tag">
                                                {cell.status === 'partial' ? 'part paid' : cell.status === 'unpaid' ? 'due' : ''}
                                            </span>
                                        </button>
                                    );
                                })}
                            </div>
                        ) : session && (
                            <div className="collection__invoices-empty">No invoiced months in this session yet.</div>
                        )}

                        {selectedMonths.length > 0 && (
                            <div className="collection__months-pay">
                                <div className="collection__months-summary">
                                    <span className="collection__months-summary-count">
                                        {selectedMonths.length} month{selectedMonths.length !== 1 ? 's' : ''}
                                    </span>
                                    <span className="collection__months-summary-range">{formatMonthRange(selectedMonths)}</span>
                                    <span className="collection__months-summary-due">
                                        {formatCurrency(monthsTotalDue)}
                                    </span>
                                </div>

                                <AdditionalFees
                                    lineItems={lineItems}
                                    lineItemErrors={lineItemErrors}
                                    lineItemsError={lineItemsError}
                                    months={selectedMonths}
                                    feeHeads={feeHeads}
                                    feeHeadsLoading={feeHeadsLoading}
                                    standardAmounts={standardAmounts}
                                    takenFeeHeadIds={takenFeeHeadIds}
                                    routes={routes}
                                    routesLoading={routesLoading}
                                    baseLabel={selectedMonths.length > 1 ? `${selectedMonths.length} months` : formatMonth(selectedMonths[0])}
                                    baseAmount={monthsDue}
                                    lineItemsNetTotal={monthsAdded}
                                    feeHeadLine={feeHeadLine}
                                    toggleFeeHead={toggleFeeHead}
                                    updateLineItem={updateLineItem}
                                    updateLineDiscount={updateLineDiscount}
                                    removeLineItem={removeLineItem}
                                    addCustomLineItem={addCustomLineItem}
                                    selectRoute={selectRoute}
                                />

                                <div className="collection__pay-field">
                                    <label className="collection__pay-label">Amount to Collect</label>
                                    <div className="collection__amount-row">
                                        <div className={`collection__pay-amount-wrap ${monthsError ? 'collection__pay-amount-wrap--error' : ''}`}>
                                            <span className="collection__pay-currency">₹</span>
                                            <input
                                                className="collection__pay-amount"
                                                type="number"
                                                min={0.01}
                                                step="0.01"
                                                value={monthsAmount}
                                                onChange={(e) => {
                                                    setMonthsAmount(e.target.value === '' ? '' : Number(e.target.value));
                                                    if (monthsError) setMonthsError('');
                                                }}
                                            />
                                        </div>
                                        <button
                                            type="button"
                                            className="collection__amount-full"
                                            onClick={() => { setMonthsAmount(monthsTotalDue); setMonthsError(''); }}
                                            title="Fill the full amount due"
                                        >
                                            <span className="collection__amount-full-label">Full</span>
                                            <span className="collection__amount-full-val">{formatCurrency(monthsTotalDue)}</span>
                                        </button>
                                    </div>

                                    {/* Neither case is an error — the API takes both. Say what will happen to the money. */}
                                    {monthsCollectingNow > 0 && monthsCollectingNow < monthsTotalDue && (
                                        <p className="collection__months-note">
                                            Short of the total — the oldest months are settled first, so the later
                                            ones stay unpaid. The fees are still billed on every selected month.
                                        </p>
                                    )}
                                    {monthsCollectingNow > monthsTotalDue && (
                                        <p className="collection__months-note collection__months-note--advance">
                                            {formatCurrency(round2(monthsCollectingNow - monthsTotalDue))} over the total — held as an advance.
                                        </p>
                                    )}
                                </div>

                                <div className="collection__pay-field">
                                    <label className="collection__pay-label">Payment Method</label>
                                    <div className="collection__method-grid">
                                        {PAYMENT_METHODS.map((m) => (
                                            <button
                                                key={m.id}
                                                type="button"
                                                className={`collection__method-btn ${paymentMethod === m.id ? 'collection__method-btn--active' : ''}`}
                                                onClick={() => setPaymentMethod(m.id)}
                                            >
                                                <span className="collection__method-icon">{m.icon}</span>
                                                <span className="collection__method-label">{m.label}</span>
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {needsReference && (
                                    <div className="collection__pay-field collection__pay-field--slide">
                                        <label className="collection__pay-label">Reference Number</label>
                                        <input
                                            className="collection__pay-ref"
                                            type="text"
                                            placeholder={paymentMethod === 'cheque' ? 'Cheque No.' : 'Transaction Ref'}
                                            maxLength={100}
                                            value={referenceNumber}
                                            onChange={(e) => setReferenceNumber(e.target.value)}
                                        />
                                    </div>
                                )}

                                {monthsError && <p className="collection__pay-error">{monthsError}</p>}

                                <button className="collection__collect-btn" onClick={handleCollectMonths} disabled={monthsCollecting}>
                                    {monthsCollecting ? (
                                        <>
                                            <svg className="collection__spinner" width="22" height="22" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" strokeWidth="3" /></svg>
                                            Processing…
                                        </>
                                    ) : (
                                        <>
                                            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></svg>
                                            Collect {formatCurrency(monthsCollectingNow)} &amp; Print Receipt
                                        </>
                                    )}
                                </button>
                            </div>
                        )}
                    </div>

                    {/* ── Invoices — the single-invoice path, and the only one that can carry
                        ad-hoc charges (the API ties line items to one invoice). ── */}
                    <h4 className="collection__months-title collection__inv-section-title">Invoices</h4>

                    {invoicesLoading ? (
                        <div className="collection__invoices-loading">
                            <svg className="collection__spinner" width="22" height="22" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" strokeWidth="3" /></svg>
                            <span>Loading invoices…</span>
                        </div>
                    ) : studentInvoices.length === 0 ? (
                        <div className="collection__invoices-empty">No invoices found for this student.</div>
                    ) : (
                        <div className="collection__invoices-grid">
                            {studentInvoices.map((inv) => {
                                const balance = parseFloat(inv.balance_amount);
                                const isPending = balance > 0;
                                return (
                                    <button
                                        key={inv.id}
                                        className={`collection__inv-card ${isPending ? 'collection__inv-card--pending' : 'collection__inv-card--paid'}`}
                                        onClick={() => isPending ? handleInvoiceSelect(inv) : undefined}
                                        disabled={!isPending}
                                    >
                                        <div className="collection__inv-card-top">
                                            <span className="collection__inv-card-inv">{inv.invoice_number}</span>
                                            <span className={`collection__inv-card-status collection__inv-card-status--${inv.status}`}>{inv.status}</span>
                                        </div>
                                        <div className="collection__inv-card-title">{inv.title}</div>
                                        <div className="collection__inv-card-row">
                                            <span className="collection__inv-card-label">Total</span>
                                            <span className="collection__inv-card-val">{formatCurrency(parseFloat(inv.total_amount))}</span>
                                        </div>
                                        <div className="collection__inv-card-row">
                                            <span className="collection__inv-card-label">Paid</span>
                                            <span className="collection__inv-card-val collection__inv-card-val--paid">{formatCurrency(parseFloat(inv.paid_amount))}</span>
                                        </div>
                                        {isPending && (
                                            <div className="collection__inv-card-row collection__inv-card-row--balance">
                                                <span className="collection__inv-card-label">Balance Due</span>
                                                <span className="collection__inv-card-val collection__inv-card-val--balance">{formatCurrency(balance)}</span>
                                            </div>
                                        )}
                                        <div className="collection__inv-card-footer">
                                            <span>Due: {formatDate(inv.due_date)}</span>
                                            <span>{inv.academic_session.name}</span>
                                        </div>
                                        {isPending && (
                                            <div className="collection__inv-card-action">Collect Payment</div>
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}

            {/* ── Split Pane (when invoice selected) ── */}
            {selectedInvoice ? (
                <div className="collection__split">
                    {/* ── Left: Work area — configure fees & take the payment ── */}
                    <div className="collection__work">
                        <div className="collection__work-head">
                            <h3 className="collection__right-title">Collect Payment</h3>
                            <span className="collection__work-for">{selectedInvoice.invoice_number} · {selectedInvoice.student_name}</span>
                        </div>

                        <div className="collection__pay-field">
                            <label className="collection__pay-label">Amount to Collect</label>
                            <div className="collection__amount-row">
                                <div className={`collection__pay-amount-wrap ${amountError ? 'collection__pay-amount-wrap--error' : ''}`}>
                                    <span className="collection__pay-currency">₹</span>
                                    <input
                                        ref={amountRef}
                                        className="collection__pay-amount"
                                        type="number"
                                        min={0}
                                        max={totalDue}
                                        value={amountPaid}
                                        onChange={(e) => { const v = e.target.value === '' ? '' : Number(e.target.value); setAmountPaid(v); if (amountError) validateAmount(v); }}
                                        onBlur={() => validateAmount(amountPaid)}
                                    />
                                </div>
                                <button
                                    type="button"
                                    className="collection__amount-full"
                                    onClick={() => { setAmountPaid(totalDue); setAmountError(''); }}
                                    title="Fill the full amount due"
                                >
                                    <span className="collection__amount-full-label">Full</span>
                                    <span className="collection__amount-full-val">{formatCurrency(totalDue)}</span>
                                </button>
                            </div>
                            {amountError && <p className="collection__pay-error">{amountError}</p>}
                        </div>

                        <AdditionalFees
                            lineItems={lineItems}
                            lineItemErrors={lineItemErrors}
                            lineItemsError={lineItemsError}
                            /* A single invoice has nothing to distribute across — no picker. */
                            months={[]}
                            feeHeads={feeHeads}
                            feeHeadsLoading={feeHeadsLoading}
                            standardAmounts={standardAmounts}
                            takenFeeHeadIds={invoiceFeeHeadIds}
                            routes={routes}
                            routesLoading={routesLoading}
                            baseLabel="Balance"
                            baseAmount={selectedInvoice.balance_amount}
                            lineItemsNetTotal={lineItemsNetTotal}
                            feeHeadLine={feeHeadLine}
                            toggleFeeHead={toggleFeeHead}
                            updateLineItem={updateLineItem}
                            updateLineDiscount={updateLineDiscount}
                            removeLineItem={removeLineItem}
                            addCustomLineItem={addCustomLineItem}
                            selectRoute={selectRoute}
                        />

                        <div className="collection__pay-field">
                            <label className="collection__pay-label">Payment Method</label>
                            <div className="collection__method-grid">
                                {PAYMENT_METHODS.map((m) => (
                                    <button
                                        key={m.id}
                                        className={`collection__method-btn ${paymentMethod === m.id ? 'collection__method-btn--active' : ''}`}
                                        onClick={() => setPaymentMethod(m.id)}
                                        type="button"
                                    >
                                        <span className="collection__method-icon">{m.icon}</span>
                                        <span className="collection__method-label">{m.label}</span>
                                    </button>
                                ))}
                            </div>
                        </div>

                        {needsReference && (
                            <div className="collection__pay-field collection__pay-field--slide">
                                <label className="collection__pay-label">Reference Number</label>
                                <input
                                    className="collection__pay-ref"
                                    type="text"
                                    placeholder={paymentMethod === 'cheque' ? 'Cheque No.' : 'Transaction Ref'}
                                    maxLength={100}
                                    value={referenceNumber}
                                    onChange={(e) => setReferenceNumber(e.target.value)}
                                    autoFocus
                                />
                            </div>
                        )}

                        <button className="collection__collect-btn" onClick={handleCollect} disabled={collecting}>
                            {collecting ? (
                                <>
                                    <svg className="collection__spinner" width="22" height="22" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" strokeWidth="3" /></svg>
                                    Processing…
                                </>
                            ) : (
                                <>
                                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></svg>
                                    Collect Payment & Print Receipt
                                </>
                            )}
                        </button>
                    </div>

                    {/* ── Right: Overview — read-only breakdown & running totals (sticky) ── */}
                    <aside className="collection__overview">
                        <div className="collection__left-header">
                            <div>
                                <h3 className="collection__inv-title">{selectedInvoice.invoice_number}</h3>
                                <p className="collection__inv-student">{selectedInvoice.student_name}</p>
                            </div>
                            <button className="collection__left-close" onClick={clearSelection} title="Change invoice">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                            </button>
                        </div>
                        <p className="collection__inv-desc">{selectedInvoice.title}</p>

                        <div className="collection__left-items">
                            <h4 className="collection__left-section">Line Items</h4>
                            {selectedInvoice.items.map((item, i) => (
                                <div className="collection__item-row" key={i}>
                                    <span className="collection__item-name">{item.name}</span>
                                    <span className="collection__item-amount">{formatCurrency(item.amount)}</span>
                                </div>
                            ))}
                            {draftLines.map((it) => {
                                const badge = it.type === 'transport' ? 'Transport' : it.feeHeadMode === 'new' ? 'New' : 'Added';
                                return (
                                    <div className="collection__item-row" key={it.key}>
                                        <span className="collection__item-name">
                                            {lineItemName(it)} <span className="collection__adhoc-badge">{badge}</span>
                                        </span>
                                        <span className="collection__item-amount">{formatCurrency(lineItemGross(it))}</span>
                                    </div>
                                );
                            })}

                            {overview.discounts.length > 0 && (
                                <>
                                    <div className="collection__item-row collection__item-row--subtotal">
                                        <span className="collection__item-name">Subtotal</span>
                                        <span className="collection__item-amount">{formatCurrency(overview.subtotal)}</span>
                                    </div>
                                    <h4 className="collection__left-section collection__left-section--discounts">Discounts</h4>
                                    {overview.discounts.map((d, i) => (
                                        <div className="collection__item-row collection__item-row--discount" key={i}>
                                            <span className="collection__item-name">{d.name}</span>
                                            <span className="collection__item-amount">− {formatCurrency(d.amount)}</span>
                                        </div>
                                    ))}
                                </>
                            )}

                            <div className="collection__item-row collection__item-row--total">
                                <span className="collection__item-name">Total</span>
                                <span className="collection__item-amount">{formatCurrency(overview.total)}</span>
                            </div>
                        </div>

                        <div className="collection__left-summary">
                            <div className="collection__summary-row">
                                <span>Paid</span><span className="collection__summary-val collection__summary-val--paid">{formatCurrency(selectedInvoice.paid_amount)}</span>
                            </div>
                            <div className="collection__summary-row collection__summary-row--balance">
                                <span>{lineItemsNetTotal > 0 ? 'Total Due (incl. added fees)' : 'Outstanding Balance'}</span>
                                <span className="collection__summary-val collection__summary-val--balance">{formatCurrency(totalDue)}</span>
                            </div>
                            <div className="collection__summary-row collection__summary-row--collect">
                                <span>Now Collecting</span>
                                <span className="collection__summary-val collection__summary-val--collect">{formatCurrency(collectingNow)}</span>
                            </div>
                            {balanceAfter > 0 && (
                                <div className="collection__summary-row">
                                    <span>Balance After</span>
                                    <span className="collection__summary-val">{formatCurrency(balanceAfter)}</span>
                                </div>
                            )}
                        </div>
                    </aside>
                </div>
            ) : (
                !studentsSearched && (
                    <div className="collection__empty-state">
                        <div className="collection__empty-icon">
                            <RupeeIcon size={56} strokeWidth={1.2} />
                        </div>
                        <h3 className="collection__empty-title">Ready to Collect</h3>
                        <p className="collection__empty-text">Search for a student or enter an invoice number in the search bar above to start collecting payment.</p>
                    </div>
                )
            )}
        </div>
    );
}
